import _ from 'lodash';
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { userActions } from '../../actions/user.actions.js';
import { useTranslation } from 'react-i18next';

import HtmlTooltip from '../HtmlTooltip';
import tooltipImg from '../../img/tooltip.png';
import { useDropzone } from 'react-dropzone';
import { CircularProgress, Button, Grid } from '@material-ui/core';
import { Delete as DeleteIcon } from '@material-ui/icons';
import BlockIcon from '@mui/icons-material/Block';
import { Collapse, Slider } from '@mui/material';

import { dragAndDropStyleNew } from './shared.styles';
import { useStyles } from './TTITrainPage.styles.js';
import { showMessageV2 } from '../../utils/page.utils';
import { statusCheckInterval } from '../../constants/app.constants.js';

import {
  fetchSubscriptionInfo,
  checkTTITrainingCompletion,
  uploadTTITraining,
  checkNsfwImage,
} from '../../services/page.services';
import { checkValidLoginStatus } from '../../utils/user.utils';
import addIcon from '../../img/addIcon.png';

import NoCreditLeftModal from '../layout/NoCreditLeftModal.js';
import TTIUpgradeModal from '../layout/TTIUpgradeModal.js';
import stepChecked from '../../img/step-checked.png';

function TTITrainPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const userId = user?.id;
  const isAdmin = user?.subscription?.type === 'ADMIN';
  const isCustom = user?.subscription?.type === 'CUSTOM';

  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [label, setLabel] = useState('');
  const [usage, setUsage] = useState('');
  const [steps, setSteps] = useState(0);
  const [thumbnail, setThumbnail] = useState('');
  const [trainingRemainingCount, setTrainingRemainingcount] = useState(null);
  const [trainingInProgress, setTrainingInProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [maxFiles, setMaxFiles] = useState(10);

  const [openNoCreditLeftModal, setOpenNoCreditLeftModal] = useState(false);
  const [openFileUpgradeModal, setOpenFileUpgradeModal] = useState(false);
  const [openStepUpgradeModal, setOpenStepUpgradeModal] = useState(false);

  const stepMarks = [
    { value: 500, label: '1' },
    { value: 1000, label: '2' },
    { value: 1500, label: '3' },
    { value: 2000, label: '4' },
    { value: 2500, label: '5' },
    { value: 3000, label: '6' },
  ];

  // tti training credits
  const calculateCoin = steps => {
    return steps <= 1000 ? 1 : steps / 500 - 1;
  };

  const coin = useMemo(() => calculateCoin(steps), [steps]);

  useEffect(() => {
    if (userId) initPage();
  }, [userId]);

  useEffect(() => {
    if (trainingRemainingCount === 0) {
      setOpenNoCreditLeftModal(true);
      return;
    }
  }, [trainingRemainingCount]);

  useEffect(() => {
    if (user.subscription?.type === 'FREE') {
      setMaxFiles(5);
    }
    if (user.subscription?.type.startsWith('BASIC')) {
      setMaxFiles(10);
    }
    if (user.subscription?.type.startsWith('PRO')) {
      setMaxFiles(30);
    }
    if (user.subscription?.type.startsWith('PREMIUM')) {
      setMaxFiles(50);
    }
    if (user.subscription?.type === 'ADMIN') {
      setMaxFiles(50);
    }
  }, [user.subscription?.type]);

  // console.log('trainingRemainingCount', trainingRemainingCount);

  useEffect(() => {
    if (files.length === 0) {
      setThumbnail('');
    }
    if (files.length > 0 && !thumbnail) {
      setThumbnail(files.filter(f => f.nsfw === false)[0]?.name);
    }
  }, [files]);

  useEffect(() => {
    const stepOneComplete = files.length > 0 && thumbnail;
    if (!stepOneComplete && !usage && !steps && !label) {
      setCurrentStep(Math.max(1, currentStep));
    } else if (stepOneComplete && !usage && !steps && !label) {
      setCurrentStep(Math.max(2, currentStep));
    } else if (stepOneComplete && usage && !steps && !label) {
      setCurrentStep(Math.max(3, currentStep));
      setSteps(500);
    } else if (stepOneComplete && usage && steps && !label) {
      setCurrentStep(Math.max(4, currentStep));
    } else if (stepOneComplete && usage && steps && label) {
      setCurrentStep(Math.max(5, currentStep));
    } else if (stepOneComplete && usage && steps && label) {
      setCurrentStep(Math.max(6, currentStep));
    }
  }, [files, label, usage, steps, thumbnail, currentStep]);

  useEffect(() => {
    return () => {
      files.forEach(file => URL.revokeObjectURL(file.preview));
    };
  }, []);

  const checkAndPollPendingTraining = async () => {
    let trainings = await checkTTITrainingCompletion();

    while (_.some(trainings, { status: 'PENDING' })) {
      setTrainingInProgress(true);
      await new Promise(resolve => setTimeout(resolve, statusCheckInterval));
      trainings = await checkTTITrainingCompletion();
    }
    await checkRemainingCount();
    setTrainingInProgress(false);
  };

  const checkRemainingCount = async () => {
    let subscriptionInfo = await fetchSubscriptionInfo();
    console.log('subscriptionInfo', subscriptionInfo);
    dispatch(userActions.updateSubscription(subscriptionInfo));
    setTrainingRemainingcount(
      (subscriptionInfo?.type === 'FREE'
        ? subscriptionInfo?.freeTtiTrainingRemaining + subscriptionInfo?.trainingCreditsRemaining
        : subscriptionInfo?.trainingCreditsRemaining) || 0
    );
  };

  const initPage = async () => {
    try {
      if (!isAdmin && !isCustom) {
        checkAndPollPendingTraining();
      }
      await checkRemainingCount();
      setFiles([]);
      setLabel('');
      setUsage('');
      setSteps(0);
      setThumbnail('');
      setCurrentStep(1);
    } catch (e) {
      showMessageV2(dispatch, t('modal.pageLoadFail'), { reloadOnClose: true });
    }
  };

  const onDropAccepted = async acceptedFiles => {
    try {
      if (checkValidLoginStatus(userId, dispatch)) {
        const numFiles = files.length + acceptedFiles.length;
        if (maxFiles < numFiles) {
          setOpenFileUpgradeModal(true);
        } else {
          const filesWithPreview = acceptedFiles.map(file =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            })
          );
          setFiles(prevState => [...prevState, ...filesWithPreview]);
          for (const file of acceptedFiles) {
            const nsfwObj = await checkNsfwImage(file);
            file.nsfw = !!nsfwObj;
            setFiles(prevState => prevState.map(f => (f.name === file.name ? file : f)));
          }
        }
      }
    } catch (e) {
      showMessageV2(dispatch, t('ttiInferenceTab.submit.tryAgain'));
      await initPage();
      setLoading(false);
    }
  };

  const onDropRejected = async input => {
    if (checkValidLoginStatus(userId, dispatch)) {
      const error = input[0].errors[0];
      if (error.code === 'file-invalid-type') {
        showMessageV2(dispatch, t('trainingTab.modal.notSupportedFileType'));
      }
      if (error.code === 'size-too-large') {
        showMessageV2(dispatch, t('ttsTrainingTab.modal.sizeTooLarge'));
      }
      if (error.code === 'too-many-files') {
        setOpenFileUpgradeModal(true);
      }
      if (error.code === 'duplicate-files') {
        showMessageV2(dispatch, t('ttsTrainingTab.modal.duplicateFiles'));
      }
    }
  };

  const fileValidator = file => {
    if (file.size > 10000000) {
      return {
        code: 'size-too-large',
        message: `file is larger than 10MB`,
      };
    }
    if (files.length) {
      for (var uploadedFile of files) {
        if (
          uploadedFile.name === file.name &&
          uploadedFile.size === file.size &&
          uploadedFile.lastModified.toString() === file.lastModified.toString()
        ) {
          return {
            code: 'duplicate-files',
            message: `duplicate files`,
          };
        }
      }
    }

    return null;
  };

  const trainDropzone = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    onDropAccepted,
    onDropRejected,
    preventDuplicates: true,
    disabled: loading,
    validator: fileValidator,
    minFiles: 5,
    maxFiles: maxFiles,
  });

  const handleDeleteFile = (e, index) => {
    e.stopPropagation();
    URL.revokeObjectURL(files[index].preview);
    const newFileList = files.filter((_file, i) => i !== index);
    setFiles(newFileList);
    if (thumbnail === files[index].name) {
      setThumbnail('');
    }
  };

  const handleSliderChange = (event, newValue) => {
    if (!user.subscription?.type.startsWith('PRO') && !user.subscription?.type.startsWith('PREMIUM')) {
      if (newValue > 500) {
        setOpenStepUpgradeModal(true);
        return;
      }
    }
    setSteps(newValue);
  };

  const handleUpload = async (files, usage, thumbnail, label, steps, subscriptionType) => {
    try {
      setLoading(true);
      await uploadTTITraining(files, usage, thumbnail, label, steps, subscriptionType);
      showMessageV2(dispatch, t('ttiTrainingTab.modal.uploadSuccess'), { reloadOnClose: true });
    } catch (e) {
      // if (e?.response?.data?.errorType === 'NSFW') {
      //   const nsfwCount = e.response.data.nsfwCount;
      //   dispatch(appActions.openCustomMessageModal('ttiNSFWError', [nsfwCount]));
      // }
      showMessageV2(dispatch, t('ttiInferenceTab.submit.tryAgain'));
    }
    await initPage();
    setLoading(false);
  };

  const readyForSubmit =
    userId &&
    !trainingInProgress &&
    trainingRemainingCount > 0 &&
    label &&
    files.length > 0 &&
    thumbnail &&
    usage &&
    steps;

  const handleSubmit = async () => {
    if (checkValidLoginStatus(userId, dispatch)) {
      if (trainingRemainingCount === 0) {
        setOpenNoCreditLeftModal(true);
        return;
      }
      if (trainingRemainingCount === 0) {
        showMessageV2(dispatch, t('trainingTab.submit.noRemaining'));
        return;
      }
      if (!readyForSubmit) return;
      for (const file of files) {
        if (file.nsfw === undefined) {
          showMessageV2(dispatch, t('ttiTrainingTab.modal.nsfwChecking'));
          return;
        }
      }
      const safeFiles = files.filter(file => file.nsfw === false);
      setFiles(safeFiles);
      await handleUpload(safeFiles, usage, thumbnail, label, steps, user.subscription.type);
    }
  };

  const classes = useStyles();
  return (
    <>
      <TTIUpgradeModal
        open={openFileUpgradeModal}
        onClose={() => setOpenFileUpgradeModal(false)}
        messages={[t('ttiTrainingTab.modal.fileUpgrade.0'), maxFiles, t('ttiTrainingTab.modal.fileUpgrade.1')]}
      />
      <TTIUpgradeModal
        open={openStepUpgradeModal}
        onClose={() => setOpenStepUpgradeModal(false)}
        messages={[t('ttiTrainingTab.modal.stepUpgrade')]}
      />
      <NoCreditLeftModal open={openNoCreditLeftModal} onClose={() => setOpenNoCreditLeftModal(false)} />
      <div className={classes.container}>
        <div className={classes.pageTitle}>{t('ttiTrainingTab.title')}</div>

        {/* 1. Image Upload */}
        <div className={classes.stepHeader}>
          {files.length > 0 && thumbnail ? (
            <img className={classes.stepHeaderChecked} src={stepChecked} alt="Step completed" />
          ) : (
            <span className={classes.stepHeaderNum}>1</span>
          )}
          <span>{t('ttiTrainingTab.stepOne.title')}</span>
          <HtmlTooltip
            title={
              <div className={'globalTooltipContainer'}>
                <div className={'globalTooltipTextContainer'}>
                  <div className={`globalTooltipText globalTooltipTextBullet`}>1.</div>
                  <div className={'globalTooltipText'}>{t('ttiTrainingTab.stepOne.tooltip.contents.0')}</div>
                </div>
                <div className={'globalTooltipTextContainer'}>
                  <div className={`globalTooltipText globalTooltipTextBullet`}>2.</div>
                  <div className={'globalTooltipText'}>{t('ttiTrainingTab.stepOne.tooltip.contents.1')}</div>
                </div>
              </div>
            }
          >
            <img className={classes.titleTooltipImg} src={tooltipImg} alt="tooltip-img" />
          </HtmlTooltip>
        </div>
        <div className={classes.section}>
          <div {...trainDropzone.getRootProps({ style: dragAndDropStyleNew })}>
            <input {...trainDropzone.getInputProps()} />
            <div className={classes.dragAndDrop}>
              <img src={addIcon} className={classes.addButtonImg} alt="Step completed" />
              <div>
                <div className={classes.dragAndDropText}>{t('ttiTrainingTab.stepOne.dragAndDropText')}</div>
                <div className={classes.dragAndDropText}>{t('ttiTrainingTab.stepOne.sizeLimitText')}</div>
                <div className={classes.dragAndDropText}>
                  <span>{t('ttiTrainingTab.stepOne.numberLimitText.0')}</span>
                  <span>{maxFiles}</span>
                  <span>{t('ttiTrainingTab.stepOne.numberLimitText.1')}</span>
                </div>
              </div>
            </div>
          </div>
          {files.length !== 0 && (
            <Grid container className={classes.dragAndDropList} style={{ display: 'flex', alignItems: 'flex-start' }}>
              {files.map((file, index) => (
                <div
                  key={index}
                  className={classes.gridItem}
                  onClick={() => {
                    if (!loading && file.nsfw === false) {
                      setThumbnail(file.name);
                    }
                  }}
                  disabled={file.nsfw !== false || loading}
                  style={file.name === thumbnail ? { backgroundColor: '#9A9A9A' } : {}}
                >
                  <img
                    src={file.preview}
                    alt={file.name}
                    className={classes.gridItemImage}
                    style={file.nsfw === false ? { opacity: 1 } : { opacity: 0.25 }}
                  />
                  {file.name === thumbnail && (
                    <div className={classes.gridThumnailTag}>{t('ttiTrainingTab.stepOne.thumbnailTag')}</div>
                  )}
                  <div>
                    {file.nsfw === undefined ? (
                      <CircularProgress className={classes.nsfwChecking} />
                    ) : (
                      file.nsfw === true && <BlockIcon className={classes.nsfwBlocked} />
                    )}
                  </div>
                  <p className={classes.gridItemText} style={file.nsfw === false ? { opacity: 1 } : { opacity: 0.25 }}>
                    {file.name}
                  </p>
                  <Button
                    onClick={e => handleDeleteFile(e, index)}
                    aria-label="delete"
                    className={classes.gridItemDeleteButton}
                    disabled={loading}
                  >
                    <DeleteIcon className={classes.gridDeleteIcon} />
                  </Button>
                </div>
              ))}
            </Grid>
          )}
          <div className={classes.gridBottomItems}>
            {files.length !== 0 && (
              <span className={classes.fileLength}>
                {files.length} / {maxFiles}
              </span>
            )}
            {files.filter(file => file.nsfw === true).length > 0 && (
              <span className={classes.nsfwBlockedText}>{t('ttiTrainingTab.stepOne.nsfwBlockMessage')}</span>
            )}
          </div>
        </div>

        {/* 2. Usage */}
        <div
          className={`${classes.stepHeader} ${currentStep < 2 && classes.noMargin} ${currentStep < 2 && classes.greyout}`}
        >
          {usage ? (
            <img className={classes.stepHeaderChecked} src={stepChecked} alt="Step completed" />
          ) : (
            <span className={classes.stepHeaderNum}>2</span>
          )}
          <span>{t('ttiTrainingTab.stepTwo.title')}</span>
          <HtmlTooltip
            title={
              <div className={'globalTooltipContainer'}>
                <div className={'globalTooltipTextContainer'}>
                  <div className={`globalTooltipText globalTooltipTextBullet`}>1.</div>
                  <div className={'globalTooltipText'}>{t('ttiTrainingTab.stepTwo.tooltip.contents.0')}</div>
                </div>
                <div className={'globalTooltipTextContainer'}>
                  <div className={`globalTooltipText globalTooltipTextBullet`}>2.</div>
                  <div className={'globalTooltipText'}>{t('ttiTrainingTab.stepTwo.tooltip.contents.1')}</div>
                </div>
              </div>
            }
          >
            <img className={classes.titleTooltipImg} src={tooltipImg} alt="tooltip-img" />
          </HtmlTooltip>
        </div>
        <Collapse in={currentStep >= 2} timeout="auto" unmountOnExit>
          <div className={classes.section}>
            <div className={classes.usageButtonContainer}>
              <Button
                noMargin
                className={classes.usageButton}
                onClick={() => {
                  if (checkValidLoginStatus(userId, dispatch)) {
                    setUsage('object');
                  }
                }}
                disabled={loading}
                style={{
                  backgroundColor: usage === 'object' ? '#fff' : '#292929',
                  color: usage === 'object' ? '#000' : '#9A9A9A',
                }}
              >
                {t('ttiTrainingTab.stepTwo.usage.0')}
              </Button>
              <Button
                className={classes.usageButton}
                onClick={() => {
                  if (checkValidLoginStatus(userId, dispatch)) {
                    setUsage('style');
                  }
                }}
                disabled={loading}
                style={{
                  backgroundColor: usage === 'style' ? '#fff' : '#292929',
                  color: usage === 'style' ? '#000' : '#9A9A9A',
                }}
              >
                {t('ttiTrainingTab.stepTwo.usage.1')}
              </Button>
            </div>
          </div>
        </Collapse>
        <div className={classes.separater} />

        {/* 3. steps */}
        <div
          className={`${classes.stepHeader} ${currentStep < 3 && classes.noMargin} ${currentStep < 3 && classes.greyout}`}
        >
          {steps ? (
            <img className={classes.stepHeaderChecked} src={stepChecked} alt="Step completed" />
          ) : (
            <span className={classes.stepHeaderNum}>3</span>
          )}
          <span>{t('ttiTrainingTab.stepThree.title')}</span>
          <HtmlTooltip
            title={
              <div className={'globalTooltipContainer'}>
                <div className={'globalTooltipTextContainer'}>
                  <div className={`globalTooltipText globalTooltipTextBullet`}>1.</div>
                  <div className={'globalTooltipText'}>{t('ttiTrainingTab.stepThree.tooltip.contents.0')}</div>
                </div>
                <div className={'globalTooltipTextContainer'}>
                  <div className={`globalTooltipText globalTooltipTextBullet`}>2.</div>
                  <div className={'globalTooltipText'}>{t('ttiTrainingTab.stepThree.tooltip.contents.1')}</div>
                </div>
              </div>
            }
          >
            <img className={classes.titleTooltipImg} src={tooltipImg} alt="tooltip-img" />
          </HtmlTooltip>
        </div>
        <Collapse in={currentStep >= 3} timeout="auto" unmountOnExit>
          <div className={classes.section}>
            <Slider
              aria-label="Restricted values"
              value={steps}
              onChange={handleSliderChange}
              step={500}
              min={500}
              max={3000}
              valueLabelDisplay="off"
              marks={stepMarks}
              disabled={loading}
              classes={{ root: classes.sliderRoot }}
            />
          </div>
        </Collapse>
        <div className={classes.separater} />

        {/* 4. label */}
        <div
          className={`${classes.stepHeader} ${currentStep < 4 && classes.noMargin} ${currentStep < 4 && classes.greyout}`}
        >
          {label ? (
            <img className={classes.stepHeaderChecked} src={stepChecked} alt="Step completed" />
          ) : (
            <span className={classes.stepHeaderNum}>4</span>
          )}
          <span>{t('ttiTrainingTab.stepFour.title')}</span>
          <HtmlTooltip
            title={
              <div className={'globalTooltipContainer'}>
                <div className={'globalTooltipTextContainer'}>
                  <div className={`globalTooltipText globalTooltipTextBullet`}>&#x2022;</div>
                  <div className={'globalTooltipText'}>{t('ttiTrainingTab.stepFour.placeholder')}</div>
                </div>
                <div className={'globalTooltipTextContainer'}>
                  <div className={`globalTooltipText globalTooltipTextBullet`}>&#x2022;</div>
                  <div className={'globalTooltipText'}>{t('ttiTrainingTab.stepFour.tooltipEx')}</div>
                </div>
              </div>
            }
          >
            <img className={classes.titleTooltipImg} src={tooltipImg} alt="tooltip-img" />
          </HtmlTooltip>
        </div>
        <Collapse in={currentStep >= 4} timeout="auto" unmountOnExit>
          <div className={classes.section}>
            <input
              onChange={e => {
                if (checkValidLoginStatus(userId, dispatch)) {
                  setLabel(e.target.value);
                }
              }}
              placeholder={t('ttiTrainingTab.stepFour.placeholder')}
              className={classes.labelInput}
              disabled={loading}
            />
          </div>
        </Collapse>
        <div className={classes.separater} />

        <div className={classes.buttonContainer}>
          {!loading && (
            <>
              <div />
              <div className={classes.submitContainer}>
                {userId && (
                  <div className={classes.infoContainer}>
                    <HtmlTooltip
                      title={
                        <div className={'globalTooltipContainer'}>
                          <div className={'globalTooltipTextContainer'}>
                            <div className={`globalTooltipText globalTooltipTextBullet`}>&#x2022;</div>
                            <div className={'globalTooltipText'}>{t('ttiTrainingTab.submit.tooltip.contents.0')}</div>
                          </div>
                          <div className={'globalTooltipTextContainer'}>
                            <div className={`globalTooltipText globalTooltipTextBullet`}>&#x2022;</div>
                            <div className={'globalTooltipText'}>{t('ttiTrainingTab.submit.tooltip.contents.1')}</div>
                          </div>
                          <div className={'globalTooltipTextContainer'}>
                            <div className={`globalTooltipText globalTooltipTextBullet`}>&#x2022;</div>
                            <div className={'globalTooltipText'}>{t('ttiTrainingTab.submit.tooltip.contents.2')}</div>
                          </div>
                          <div className={'globalTooltipTextContainer'}>
                            <div className={`globalTooltipText globalTooltipTextBullet`}>&#x2022;</div>
                            <div className={'globalTooltipText'}>{t('ttiTrainingTab.submit.tooltip.contents.3')}</div>
                          </div>
                        </div>
                      }
                    >
                      <img className={classes.tooltipImg} src={tooltipImg} alt="tooltip-img" />
                    </HtmlTooltip>
                    <div className={classes.coinContainer}>
                      <div className={classes.coin}>
                        {t('ttiInferenceTab.submit.requiredCredits')}: {coin}
                      </div>
                    </div>
                  </div>
                )}

                {trainingInProgress ? (
                  <div className={classes.progressButton}>
                    <>
                      <div>{t('trainingTab.submit.inProgress')}</div>
                      <CircularProgress className={classes.loadingCircle} />
                    </>
                  </div>
                ) : (
                  <div
                    className={`${classes.button} ${!readyForSubmit && classes.disabledButton}`}
                    onClick={handleSubmit}
                  >
                    {userId && trainingRemainingCount <= 0
                      ? t('trainingTab.submit.noRemaining')
                      : t('trainingTab.submit.start')}
                  </div>
                )}
              </div>
            </>
          )}
          {loading && (
            <div className={classes.loadingContainer}>
              <CircularProgress size="1.4rem" />
              <span className={classes.loadingText}>{t('trainingTab.submit.uploading')}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default TTITrainPage;
